















































































































































































































































































































import cloneDeep from 'lodash/cloneDeep';
import CoDateRangePicker from '@/components/Atoms/co-date-range-picker/CoDateRangePicker.vue';
import CoMultiSelect from '@/components/Molecules/co-multiselect/CoMultiSelect.vue';
import CoSelect from '@/components/Molecules/co-select/CoSelect.vue';
import CoInput from '@/components/Molecules/co-input/CoInput.vue';
import CoModal from '@/components/Organisms/co-modal/CoModal.vue';
import CoTagList from '@/components/Organisms/co-TagList/coTagList.vue';
import CoButton from '@/components/Atoms/co-button/CoButton.vue';
import CoRoundButton from '@/components/Atoms/co-round-button/CoRoundButton.vue';

import type {
    UserCustomFieldDataType,
    UserCustomFieldDefinition,
    UserCustomFieldFilterOperator,
    UserCustomFieldFilterValue,
    UserDateRangeValue,
    UserFilterFieldKey,
    UserFilterOption,
    UserFilterValue,
} from './models.ts';
import CoIcon from '@/components/Atoms/co-icon/CoIcon.vue';

const DEFAULT_FILTER_VALUE: UserFilterValue = {
    search: '',
    createdAt: null,
    status: null,
    currentPlanId: null,
    moreFilters: [],
};

export default {
    name: 'CoUserFilter',
    components: {
        CoButton,
        CoDateRangePicker,
        CoMultiSelect,
        CoSelect,
        CoInput,
        CoModal,
        CoTagList,
        CoRoundButton,
    },
    props: {
        value: {
            type: Object as () => UserFilterValue,
            default: () => cloneDeep(DEFAULT_FILTER_VALUE),
        },
        planOptions: {
            type: Array as () => UserFilterOption[],
            default: () => [],
        },
        upgradeOptions: {
            type: Array as () => UserFilterOption[],
            default: () => [],
        },
        customFieldDefinitions: {
            type: Array as () => UserCustomFieldDefinition[],
            default: () => [],
        },
    },
    data() {
        return {
            // Keep a local editable copy so each chip editor can update incrementally before the parent consumes it.
            draftValue: cloneDeep({ ...DEFAULT_FILTER_VALUE, ...(this.value || {}) }) as UserFilterValue,
            activeField: null as UserFilterFieldKey | null,
            // The modal edits a single pending custom-field filter and only persists it when the user saves it.
            moreFilterDraft: null as UserCustomFieldFilterValue | null,
            isMoreFiltersModalOpen: false,
            editingMoreFilterIndex: null as number | null,
        };
    },
    watch: {
        value: {
            handler(newValue: UserFilterValue) {
                this.draftValue = cloneDeep({ ...DEFAULT_FILTER_VALUE, ...(newValue || {}) });
            },
            deep: true,
        },
        activeField(newValue: UserFilterFieldKey | null, oldValue: UserFilterFieldKey | null) {
            if (oldValue) {
                this.getFilterTippy(oldValue)?.hide();
            }

            if (newValue) {
                this.$nextTick(() => {
                    this.getFilterTippy(newValue)?.show();
                });
            }
        },
    },
    computed: {
        statusOptions(): UserFilterOption[] {
            return [0, 1, 2, 3, 4, 5, 6, 7, 8].map((statusValue) => ({
                Name: this.$t(`userstatuslabels.${statusValue}`),
                Value: statusValue,
            }));
        },
        salutationOptions(): UserFilterOption[] {
            return [
                {
                    Name: this.$t('labels.prefernottosay'),
                    Value: 'u',
                },
                {
                    Name: this.$t('labels.nonbinary'),
                    Value: 'x',
                },
                {
                    Name: this.$t('labels.female'),
                    Value: 'f',
                },
                {
                    Name: this.$t('labels.male'),
                    Value: 'm',
                },
            ];
        },
        savedMoreFilters(): UserCustomFieldFilterValue[] {
            return this.draftValue.moreFilters || [];
        },
        moreFilterFieldOptions(): UserFilterOption[] {
            return this.moreFilterDefinitions
                .map((field) => ({
                    Name: this.getCustomFieldLabel(field),
                    Value: this.getCustomFieldKey(field),
                }))
                .filter((option) => Boolean(option.Name) && Boolean(option.Value));
        },
        moreFilterDefinitions(): UserCustomFieldDefinition[] {
            return [
                {
                    Identifier: 'First_Name',
                    Label: this.$t('labels.namefirst'),
                    InputType: 'text',
                },
                {
                    Identifier: 'Last_Name',
                    Label: this.$t('labels.namelast'),
                    InputType: 'text',
                },
                {
                    Identifier: 'Email',
                    Label: this.$t('labels.email'),
                    InputType: 'text',
                },
                {
                    Identifier: 'BirthDate',
                    Label: this.$t('labels.birthdate'),
                    InputType: 'date',
                },
                {
                    Identifier: 'Interests',
                    Label: this.$t('labels.aboutme'),
                    InputType: 'text',
                },
                {
                    Identifier: 'Bio',
                    Label: this.$t('labels.headline'),
                    InputType: 'text',
                },
                {
                    Identifier: 'Tags',
                    Label: this.tagsFieldLabel,
                    InputType: 'multiselect',
                },
                {
                    Identifier: 'Salutation',
                    Label: this.$t('labels.gender'),
                    InputType: 'multiselect',
                },
                {
                    Identifier: 'Language',
                    Label: this.$t('labels.language'),
                    InputType: 'text',
                },
                {
                    Identifier: 'CreatedAt',
                    Label: this.$t('labels.created'),
                    InputType: 'date',
                },
                {
                    Identifier: 'UpdatedAt',
                    Label: this.$t('labels.updated'),
                    InputType: 'date',
                },
                {
                    Identifier: 'Last_Activity',
                    Label: this.$t('labels.lastactivity'),
                    InputType: 'date',
                },
                {
                    Identifier: 'Status',
                    Label: this.$t('labels.status'),
                    InputType: 'multiselect',
                },
                {
                    Identifier: 'billing_address_company',
                    Label: this.$t('labels.billingcompany'),
                    InputType: 'text',
                },
                {
                    Identifier: 'billing_address_additional_info',
                    Label: this.$t('labels.billingadditionalinfo'),
                    InputType: 'text',
                },
                {
                    Identifier: 'billing_address',
                    Label: this.$t('labels.billingaddress'),
                    InputType: 'text',
                },
                {
                    Identifier: 'billing_address_postal_code',
                    Label: this.$t('labels.billingpostalcode'),
                    InputType: 'text',
                },
                {
                    Identifier: 'billing_address_city',
                    Label: this.$t('labels.billingcity'),
                    InputType: 'text',
                },
                {
                    Identifier: 'billing_address_country',
                    Label: this.$t('labels.billingcountry'),
                    InputType: 'text',
                },
                {
                    Identifier: 'billing_address_phone',
                    Label: this.$t('labels.billingphone'),
                    InputType: 'text',
                },
                {
                    Identifier: 'billing_address_vat',
                    Label: this.$t('labels.billingvatid'),
                    InputType: 'text',
                },
                {
                    Identifier: 'address_company',
                    Label: this.$t('labels.registrationcompany'),
                    InputType: 'text',
                },
                {
                    Identifier: 'address_additional_info',
                    Label: this.$t('labels.registrationadditionalinfo'),
                    InputType: 'text',
                },
                {
                    Identifier: 'address',
                    Label: this.$t('labels.registrationaddress'),
                    InputType: 'text',
                },
                {
                    Identifier: 'address_postal_code',
                    Label: this.$t('labels.registrationpostalcode'),
                    InputType: 'text',
                },
                {
                    Identifier: 'address_city',
                    Label: this.$t('labels.registrationcity'),
                    InputType: 'text',
                },
                {
                    Identifier: 'address_country',
                    Label: this.$t('labels.registrationcountry'),
                    InputType: 'text',
                },
                {
                    Identifier: 'address_phone',
                    Label: this.$t('labels.registrationphone'),
                    InputType: 'text',
                },
                {
                    Identifier: 'CurrentPlanID',
                    Label: this.$t('labels.currentplan'),
                    InputType: 'multiselect',
                },
                {
                    Identifier: 'CurrentPlanUpgrades',
                    // Reuse localized building blocks so the upgrade filter labels stay consistent across languages.
                    Label: `${this.$t('labels.currentplan')} ${this.$t('labels.upgrades')}`,
                    InputType: 'multiselect',
                },
                {
                    Identifier: 'CurrentPlanStart',
                    Label: this.$t('labels.currentplanstart'),
                    InputType: 'date',
                },
                {
                    Identifier: 'CurrentPlanEnd',
                    Label: this.$t('labels.currentplanend'),
                    InputType: 'date',
                },
                {
                    Identifier: 'UpcomingPlanID',
                    Label: this.$t('labels.planupcoming'),
                    InputType: 'multiselect',
                },
                {
                    Identifier: 'UpcomingPlanUpgrades',
                    // Mirror the current-plan upgrade label pattern for upcoming plan upgrade filters.
                    Label: `${this.$t('labels.planupcoming')} ${this.$t('labels.upgrades')}`,
                    InputType: 'multiselect',
                },
                {
                    Identifier: 'UpcomingPlanStart',
                    Label: this.$t('labels.planupcomingstart'),
                    InputType: 'date',
                },

                ...this.customFieldDefinitions,
            ];
        },
        selectedMoreFilterFieldDefinition(): UserCustomFieldDefinition | null {
            const fieldKey = this.moreFilterDraft?.fieldKey;
            if (!fieldKey) return null;

            return this.moreFilterDefinitions.find((field) => this.getCustomFieldKey(field) === fieldKey) || null;
        },
        selectedMoreFilterFieldOptions(): UserFilterOption[] {
            if (!this.selectedMoreFilterFieldDefinition) {
                return [];
            }

            return [
                {
                    Name: this.getCustomFieldLabel(this.selectedMoreFilterFieldDefinition),
                    Value: this.getCustomFieldKey(this.selectedMoreFilterFieldDefinition),
                },
            ];
        },
        selectedMoreFilterFieldType(): UserCustomFieldDataType | null {
            return this.getCustomFieldType(this.selectedMoreFilterFieldDefinition);
        },
        moreFilterOperatorOptions(): UserFilterOption[] {
            return this.getOperatorOptionsForFieldType(this.selectedMoreFilterFieldType);
        },
        selectedMoreFilterOperator(): UserFilterOption | null {
            const operator = this.moreFilterDraft?.operator;
            return this.moreFilterOperatorOptions.find((item) => item.Value === operator) || null;
        },
        isMoreFilterBetween(): boolean {
            return this.moreFilterDraft?.operator === 'BETWEEN';
        },
        moreFilterSelectableValues(): UserFilterOption[] {
            return this.getSelectableOptionsForField(this.selectedMoreFilterFieldDefinition);
        },
        moreFilterBooleanOptions(): UserFilterOption[] {
            return [
                { Name: this.$t('labels.yes'), Value: 'true' },
                { Name: this.$t('labels.no'), Value: 'false' },
            ];
        },
        tagsFieldLabel(): string {
            return this.$unleash && this.$unleash.isEnabled('frontend.tags-are-called-topics')
                ? this.$t('labels.topics')
                : this.$t('labels.tags');
        },
        isTagsMoreFilterField(): boolean {
            return this.moreFilterDraft?.fieldKey === 'Tags';
        },
        moreFilterTagValues(): string[] {
            return (this.moreFilterDraft?.values || []).map((option) => String(option.Value)).filter(Boolean);
        },
        moreFilterTagListKey(): string {
            return this.moreFilterTagValues.join('|');
        },
        selectedMoreFilterSingleValue(): UserFilterOption | null {
            const currentValue = this.moreFilterDraft?.value;
            return this.moreFilterSelectableValues.find((item) => item.Value === currentValue) || null;
        },
        selectedMoreFilterBooleanValue(): UserFilterOption | null {
            const currentValue = this.moreFilterDraft?.value;
            return this.moreFilterBooleanOptions.find((item) => item.Value === String(currentValue)) || null;
        },
        moreFilterValueAsString(): string {
            const currentValue = this.moreFilterDraft?.value;
            return currentValue === null || currentValue === undefined ? '' : String(currentValue);
        },
        moreFilterSecondValueAsString(): string {
            const currentValue = this.moreFilterDraft?.secondValue;
            return currentValue === null || currentValue === undefined ? '' : String(currentValue);
        },
        moreFilterDatePickerValue(): UserDateRangeValue {
            return {
                startDate: this.moreFilterDraft?.value || new Date(),
                endDate: this.moreFilterDraft?.secondValue || this.moreFilterDraft?.value || new Date(),
            };
        },
        hasActiveFilters(): boolean {
            return (
                this.hasValue('createdAt') ||
                this.hasValue('status') ||
                this.hasValue('currentPlanId') ||
                this.hasValue('moreFilters')
            );
        },
    },
    methods: {
        triggerfilterPanel(fieldKey: UserFilterFieldKey) {
            if (this.activeField === fieldKey) {
                this.getFilterTippy(fieldKey)?.hide();
                this.activeField = null;
            } else {
                this.activeField = fieldKey;
            }
        },
        getFilterTippy(fieldKey: UserFilterFieldKey) {
            const tippyRef = this.$refs[`filterTippy-${fieldKey}`];
            return Array.isArray(tippyRef) ? tippyRef[0] : tippyRef;
        },
        emitValue() {
            const nextValue = cloneDeep(this.draftValue);
            this.$emit('input', nextValue);
            this.$emit('update', nextValue);
        },
        getFieldLabel(fieldKey: UserFilterFieldKey): string {
            const labels: Record<UserFilterFieldKey, string> = {
                createdAt: this.$t('labels.created'),
                status: this.$t('labels.status'),
                currentPlanId: this.$t('labels.currentplan'),
            };

            return labels[fieldKey] || fieldKey;
        },
        getCustomFieldKey(field: UserCustomFieldDefinition | null | undefined): string {
            return String(field?.Identifier || field?.identifier || '');
        },
        getCustomFieldLabel(field: UserCustomFieldDefinition | null | undefined): string {
            return String(field?.Label || field?.label || this.getCustomFieldKey(field));
        },
        getCustomFieldType(field: UserCustomFieldDefinition | null | undefined): UserCustomFieldDataType | null {
            return field?.InputType || field?.data_type || null;
        },
        getCustomFieldOptions(field: UserCustomFieldDefinition | null | undefined): string[] {
            return [...(field?.Options || field?.OptionsValues || field?.options_values || [])].filter(Boolean);
        },
        getSelectableOptionsForField(field: UserCustomFieldDefinition | null | undefined): UserFilterOption[] {
            const fieldKey = this.getCustomFieldKey(field);

            if (fieldKey === 'Status') {
                return this.statusOptions;
            }

            if (
                fieldKey === 'CurrentPlanID' ||
                fieldKey === 'UpcomingPlanID' ||
                fieldKey === 'UpcomingPlanUpgrades' ||
                fieldKey === 'CurrentPlanUpgrades'
            ) {
                // Plan and plan-upgrade filters share the same select UI but read from different option lists.
                if (fieldKey === 'UpcomingPlanUpgrades' || fieldKey === 'CurrentPlanUpgrades') {
                    return this.upgradeOptions;
                }

                return this.planOptions;
            }

            if (fieldKey === 'Salutation') {
                return this.salutationOptions;
            }

            return this.getCustomFieldOptions(field).map((value) => ({
                Name: value,
                Value: value,
            }));
        },
        getOperatorOptionsForFieldType(fieldType: UserCustomFieldDataType | null): UserFilterOption[] {
            if (fieldType === 'number' || fieldType === 'date') {
                return [
                    { Name: this.$t('labels.userFilters.equal'), Value: 'EQUAL' },
                    { Name: this.$t('labels.userFilters.notEqual'), Value: 'NOT_EQUAL' },
                    { Name: this.$t('labels.userFilters.greaterThan'), Value: 'GREATER_THAN' },
                    { Name: this.$t('labels.userFilters.lessThan'), Value: 'LESS_THAN' },
                    { Name: this.$t('labels.userFilters.greaterThanOrEqual'), Value: 'GREATER_THAN_OR_EQUAL' },
                    { Name: this.$t('labels.userFilters.lessThanOrEqual'), Value: 'LESS_THAN_OR_EQUAL' },
                    { Name: this.$t('labels.userFilters.between'), Value: 'BETWEEN' },
                ];
            }

            if (fieldType === 'text' || fieldType === 'link') {
                return [
                    { Name: this.$t('labels.userFilters.like'), Value: 'ILIKE' },
                    { Name: this.$t('labels.userFilters.notLike'), Value: 'NOT_ILIKE' },
                    { Name: this.$t('labels.userFilters.equal'), Value: 'EQUAL' },
                ];
            }

            if (fieldType === 'multiselect') {
                return [
                    { Name: this.$t('labels.userFilters.inUsersValues'), Value: 'IN' },
                    { Name: this.$t('labels.userFilters.notInUsersValues'), Value: 'NOT_IN' },
                ];
            }

            if (fieldType === 'select' || fieldType === 'boolean') {
                return [
                    { Name: this.$t('labels.userFilters.equal'), Value: 'EQUAL' },
                    { Name: this.$t('labels.userFilters.notEqual'), Value: 'NOT_EQUAL' },
                ];
            }

            return [];
        },
        getDefaultMoreFilterOperator(fieldType: UserCustomFieldDataType): UserCustomFieldFilterOperator {
            if (fieldType === 'number' || fieldType === 'date') return 'EQUAL';
            if (fieldType === 'text' || fieldType === 'link') return 'ILIKE';
            if (fieldType === 'multiselect') return 'IN';
            return 'EQUAL';
        },
        buildDefaultMoreFilter(field: UserCustomFieldDefinition): UserCustomFieldFilterValue {
            const fieldType = this.getCustomFieldType(field) || 'text';

            return {
                fieldKey: this.getCustomFieldKey(field),
                fieldLabel: this.getCustomFieldLabel(field),
                fieldType,
                operator: this.getDefaultMoreFilterOperator(fieldType),
                value: null,
                secondValue: null,
                values: [],
            };
        },
        hasValue(fieldKey: UserFilterFieldKey | 'moreFilters'): boolean {
            if (fieldKey === 'createdAt') {
                return !!this.draftValue.createdAt?.value?.startDate;
            }

            if (fieldKey === 'status') {
                return (this.draftValue.status?.values || []).length > 0;
            }

            if (fieldKey === 'currentPlanId') {
                return (this.draftValue.currentPlanId?.values || []).length > 0;
            }

            if (fieldKey === 'moreFilters') {
                return this.savedMoreFilters.length > 0;
            }

            return false;
        },
        hasCompleteMoreFilterValue(filter: UserCustomFieldFilterValue | null): boolean {
            if (!filter?.fieldKey || !filter.operator || !filter.fieldType) {
                return false;
            }

            if (filter.fieldType === 'multiselect') {
                return filter.values.length > 0;
            }

            if (filter.operator === 'BETWEEN') {
                return (
                    filter.value !== null &&
                    filter.value !== '' &&
                    filter.secondValue !== null &&
                    filter.secondValue !== ''
                );
            }

            return filter.value !== null && filter.value !== '';
        },
        formatDateLabel(value: Date | string | number | null | undefined): string {
            if (!value) return '';
            const parsedDate = new Date(value);
            if (Number.isNaN(parsedDate.getTime())) return '';
            return parsedDate.toLocaleDateString();
        },
        formatMultiValueLabel(values: UserFilterOption[] = []): string {
            if (!values.length) return '';
            const visibleValues = values.slice(0, 2).map((item) => item.Name);
            const remainingCount = values.length - visibleValues.length;
            return remainingCount > 0 ? `${visibleValues.join(', ')} +${remainingCount}` : visibleValues.join(', ');
        },
        formatMoreFilterValueLabel(filter: UserCustomFieldFilterValue): string {
            if (filter.fieldType === 'multiselect') {
                return this.formatMultiValueLabel(filter.values);
            }

            if (filter.fieldType === 'date') {
                if (filter.operator === 'BETWEEN') {
                    return `${this.formatDateLabel(filter.value)} - ${this.formatDateLabel(filter.secondValue)}`;
                }

                return this.formatDateLabel(filter.value);
            }

            if (filter.operator === 'BETWEEN') {
                return `${filter.value} - ${filter.secondValue}`;
            }

            return String(filter.value);
        },
        formatMoreFilterChipLabel(filter: UserCustomFieldFilterValue): string {
            const operatorLabel = this.getOperatorOptionsForFieldType(filter.fieldType).find(
                (option) => option.Value === filter.operator
            )?.Name;
            return `${filter.fieldLabel} ${operatorLabel} ${this.formatMoreFilterValueLabel(filter)}`.trim();
        },
        getMoreFilterSignature(filter: UserCustomFieldFilterValue): string {
            const valuesKey =
                filter.fieldType === 'multiselect'
                    ? filter.values
                          .map((item) => item.Value)
                          .sort()
                          .join(',')
                    : `${filter.value ?? ''}|${filter.secondValue ?? ''}`;
            return [filter.fieldKey, filter.operator, valuesKey].join('::');
        },
        openMoreFiltersModal() {
            this.resetMoreFilterDraft();
            this.isMoreFiltersModalOpen = true;
            this.$refs.moreFiltersModal?.show();
        },
        closeMoreFiltersModal() {
            this.isMoreFiltersModalOpen = false;
            this.moreFilterDraft = null;
            this.editingMoreFilterIndex = null;
        },
        resetMoreFilterDraft() {
            this.moreFilterDraft = null;
            this.editingMoreFilterIndex = null;
        },
        saveMoreFilter() {
            if (!this.hasCompleteMoreFilterValue(this.moreFilterDraft) || !this.moreFilterDraft) {
                return;
            }

            const nextFilter = cloneDeep(this.moreFilterDraft);
            const nextSignature = this.getMoreFilterSignature(nextFilter);
            const nextFilters = [...this.savedMoreFilters];

            if (this.editingMoreFilterIndex !== null) {
                nextFilters.splice(this.editingMoreFilterIndex, 1, nextFilter);
                const duplicateIndex = nextFilters.findIndex(
                    (filter, index) =>
                        index !== this.editingMoreFilterIndex && this.getMoreFilterSignature(filter) === nextSignature
                );

                if (duplicateIndex !== -1) {
                    nextFilters.splice(duplicateIndex, 1);
                }
            } else if (!nextFilters.some((filter) => this.getMoreFilterSignature(filter) === nextSignature)) {
                nextFilters.push(nextFilter);
            }

            this.draftValue.moreFilters = nextFilters;
            this.emitValue();
            this.$refs.moreFiltersModal?.hide();
        },
        editSavedMoreFilter(index: number) {
            const filterToEdit = this.savedMoreFilters[index];
            if (!filterToEdit) {
                return;
            }

            this.editingMoreFilterIndex = index;
            this.moreFilterDraft = cloneDeep(filterToEdit);
            this.isMoreFiltersModalOpen = true;
            this.$refs.moreFiltersModal?.show();
        },
        removeSavedMoreFilter(index: number) {
            this.draftValue.moreFilters = this.savedMoreFilters.filter((_, currentIndex) => currentIndex !== index);
            this.emitValue();
        },

        updateMoreFilterFieldSelection(nextValues: UserFilterOption[]) {
            const selectedOption = nextValues[nextValues.length - 1];

            if (!selectedOption) {
                this.moreFilterDraft = null;
                return;
            }

            const selectedField =
                this.moreFilterDefinitions.find((field) => this.getCustomFieldKey(field) === selectedOption.Value) ||
                null;
            this.moreFilterDraft = selectedField ? this.buildDefaultMoreFilter(selectedField) : null;
        },
        updateMoreFilterOperator(selectedOption: UserFilterOption) {
            if (!this.moreFilterDraft) return;

            this.moreFilterDraft = {
                ...this.moreFilterDraft,
                operator: selectedOption.Value as UserCustomFieldFilterOperator,
            };
        },
        normalizeMoreFilterScalarValue(rawValue: string, fieldType: UserCustomFieldDataType | null) {
            if (rawValue === '') return null;
            if (fieldType === 'number') return Number(rawValue);
            return rawValue;
        },
        updateMoreFilterInputValue(target: 'value' | 'secondValue', nextValue: string) {
            if (!this.moreFilterDraft) return;

            this.moreFilterDraft = {
                ...this.moreFilterDraft,
                [target]: this.normalizeMoreFilterScalarValue(nextValue, this.moreFilterDraft.fieldType),
            };
        },
        updateMoreFilterDateValue(nextValue: UserDateRangeValue) {
            if (!this.moreFilterDraft) return;

            if (this.moreFilterDraft.operator !== 'BETWEEN') {
                this.moreFilterDraft = {
                    ...this.moreFilterDraft,
                    value: nextValue.startDate || nextValue.endDate || null,
                    secondValue: null,
                    values: [],
                };
                return;
            }

            const normalizedValue =
                this.moreFilterDraft.operator === 'BETWEEN'
                    ? nextValue
                    : {
                          startDate: nextValue?.startDate || nextValue?.endDate || null,
                          endDate: nextValue?.startDate || nextValue?.endDate || null,
                      };

            this.moreFilterDraft = {
                ...this.moreFilterDraft,
                value: normalizedValue.startDate || null,
                secondValue: normalizedValue.endDate || null,
                values: [],
            };
        },
        updateMoreFilterSingleSelectValue(selectedOption: UserFilterOption | null) {
            if (!this.moreFilterDraft) return;

            this.moreFilterDraft = {
                ...this.moreFilterDraft,
                value: selectedOption ? selectedOption.Value : null,
                secondValue: null,
                values: [],
            };
        },
        updateMoreFilterMultiValues(nextValues: UserFilterOption[]) {
            if (!this.moreFilterDraft) return;

            this.moreFilterDraft = {
                ...this.moreFilterDraft,
                value: null,
                secondValue: null,
                values: nextValues,
            };
        },
        updateMoreFilterTagValues(nextTags: string[]) {
            if (!this.moreFilterDraft) return;

            // Normalize tag suggestions into the same option shape used by the saved filter payload.
            const normalizedValues = Array.from(
                new Set((nextTags || []).map((tag) => String(tag).trim()).filter(Boolean))
            ).map((tag) => ({
                Name: tag,
                Value: tag,
            }));

            this.updateMoreFilterMultiValues(normalizedValues);
        },
        updateMoreFilterBooleanValue(selectedOption: UserFilterOption | null) {
            if (!this.moreFilterDraft) return;

            this.moreFilterDraft = {
                ...this.moreFilterDraft,
                value: selectedOption ? selectedOption.Value : null,
                secondValue: null,
                values: [],
            };
        },
        clearField(fieldKey: UserFilterFieldKey) {
            if (fieldKey === 'createdAt') {
                this.draftValue.createdAt = null;
            }

            if (fieldKey === 'status') {
                this.draftValue.status = null;
            }

            if (fieldKey === 'currentPlanId') {
                this.draftValue.currentPlanId = null;
            }

            this.emitValue();
            if (this.activeField === fieldKey) {
                this.activeField = null;
            }
        },
        clearActiveField() {
            if (!this.activeField) return;
            this.clearField(this.activeField);
        },
        clearAllFilters() {
            // Preserve the page-level search field and only reset chip-based filters owned by this organism.
            this.draftValue = {
                ...cloneDeep(DEFAULT_FILTER_VALUE),
                search: this.draftValue.search,
            };
            this.activeField = null;
            this.moreFilterDraft = null;
            this.isMoreFiltersModalOpen = false;
            this.editingMoreFilterIndex = null;
            this.$refs.moreFiltersModal?.hide();
            this.emitValue();
        },
    },
};
